import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PersonalNotes from '../PersonalNotes/PersonalNotes';

const API = process.env.REACT_APP_API_URL;

const SocialActions = ({
  contentId,
  contentType,
  title,
  content,
  showBookmark = true,
  showShare = true,
  showLike = true,
  showNotes = true,
  className = ""
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkCount, setBookmarkCount] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [hasNotes, setHasNotes] = useState(false);

  const fetchCounts = useCallback(async () => {
    try {
      const params = new URLSearchParams({ contentId, contentType });
      if (user?.uid) params.append('userId', user.uid);
      const res = await fetch(`${API}/interactions?${params}`);
      if (res.ok) {
        const data = await res.json();
        setLikeCount(data.likes || 0);
        setBookmarkCount(data.bookmarks || 0);
        setIsLiked(data.userLiked || false);
        setIsBookmarked(data.userBookmarked || false);
      }
    } catch {
      // Fallback to localStorage
      const savedLikes = JSON.parse(localStorage.getItem('gitsawe_likes') || '{}');
      const savedBookmarks = JSON.parse(localStorage.getItem('gitsawe_bookmarks') || '{}');
      const key = `${contentType}_${contentId}`;
      setIsLiked(savedLikes[key] || false);
      setIsBookmarked(savedBookmarks[key] || false);
    }
  }, [contentId, contentType, user]);

  useEffect(() => {
    fetchCounts();
    // Notes from localStorage
    const savedNotes = JSON.parse(localStorage.getItem('gitsawe_personal_notes') || '{}');
    setHasNotes((savedNotes[`${contentType}_${contentId}`] || []).length > 0);
  }, [fetchCounts, contentId, contentType]);

  const requireLogin = () => {
    toast.info('እባክዎ ለዚህ ተግባር ይግቡ');
    navigate('/login');
  };

  const handleLike = async () => {
    if (!user) return requireLogin();
    // Optimistic update
    const newLiked = !isLiked;
    setIsLiked(newLiked);
    setLikeCount(c => newLiked ? c + 1 : Math.max(0, c - 1));
    try {
      const res = await fetch(`${API}/interactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          userEmail: user.email,
          contentId, contentType, title,
          action: 'like'
        })
      });
      if (res.ok) {
        const data = await res.json();
        setIsLiked(data.active);
        setLikeCount(data.count);
        toast.success(data.active ? '❤️ ወድጄዋለሁ!' : '💔 ወዳጅነት ተወግዷል');
      }
    } catch {
      fetchCounts(); // revert on error
    }
  };

  const handleBookmark = async () => {
    if (!user) return requireLogin();
    const newBookmarked = !isBookmarked;
    setIsBookmarked(newBookmarked);
    setBookmarkCount(c => newBookmarked ? c + 1 : Math.max(0, c - 1));
    try {
      const res = await fetch(`${API}/interactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          userEmail: user.email,
          contentId, contentType, title,
          content: content || '',
          action: 'bookmark'
        })
      });
      if (res.ok) {
        const data = await res.json();
        setIsBookmarked(data.active);
        setBookmarkCount(data.count);
        toast.success(data.active ? '🔖 ወደ ምርጦች ተጨምሯል!' : '🗑️ ከምርጦች ተወግዷል');
      }
    } catch {
      fetchCounts();
    }
  };

  const handleShare = (platform) => {
    const shareText = `${title}\n\n${content?.substring(0, 300)}...\n\nከ ግጻዌ እና ስንክሳር`;
    const shareUrl = window.location.href;
    const urls = {
      telegram: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`,
    };
    if (platform === 'copy') {
      navigator.clipboard.writeText(shareText + '\n\n' + shareUrl).then(() => toast.success('📋 ተቀድቷል!'));
    } else if (urls[platform]) {
      window.open(urls[platform], '_blank');
    }
    setShowShareMenu(false);
  };

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      {showLike && (
        <button onClick={handleLike}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm transition-all ${isLiked ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-red-50 hover:text-red-600'}`}>
          <span>{isLiked ? '❤️' : '🤍'}</span>
          {likeCount > 0 && <span className="text-xs font-medium">{likeCount}</span>}
        </button>
      )}

      {showBookmark && (
        <button onClick={handleBookmark}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm transition-all ${isBookmarked ? 'bg-yellow-50 text-yellow-600 border border-yellow-200' : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-yellow-50 hover:text-yellow-600'}`}>
          <span>{isBookmarked ? '🔖' : '📑'}</span>
          {bookmarkCount > 0 && <span className="text-xs font-medium">{bookmarkCount}</span>}
          <span className="text-xs hidden sm:inline">{isBookmarked ? 'ተቀምጧል' : 'ቀምጥ'}</span>
        </button>
      )}

      {showNotes && (
        <button onClick={() => setShowNotesModal(true)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm transition-all ${hasNotes ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-green-50 hover:text-green-600'}`}>
          <span>{hasNotes ? '📝' : '📄'}</span>
          <span className="text-xs hidden sm:inline">ማስታወሻ</span>
        </button>
      )}

      {showShare && (
        <div className="relative">
          <button onClick={() => setShowShareMenu(!showShareMenu)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 transition-all">
            <span>📤</span>
            <span className="text-xs hidden sm:inline">አጋራ</span>
          </button>
          {showShareMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowShareMenu(false)} />
              <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-40">
                {[['telegram','📱','Telegram'],['whatsapp','💬','WhatsApp'],['facebook','📘','Facebook'],['twitter','🐦','Twitter'],['email','📧','Email'],['copy','📋','ቅዳ']].map(([p,icon,label]) => (
                  <button key={p} onClick={() => handleShare(p)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg">
                    <span>{icon}</span>{label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <PersonalNotes contentId={contentId} contentType={contentType} title={title}
        isOpen={showNotesModal} onClose={() => {
          setShowNotesModal(false);
          const n = JSON.parse(localStorage.getItem('gitsawe_personal_notes') || '{}');
          setHasNotes((n[`${contentType}_${contentId}`] || []).length > 0);
        }} />
    </div>
  );
};

export default SocialActions;
